<script lang="ts">
  import Icon from "../lib/ds/Icon.svelte";
  import { onDestroy } from 'svelte';
  import { RowDragger, type DragView } from '../lib/dragsort';
  import { HeldView, type NavGroup } from '../lib/navlist';
  import { STATES } from '../lib/ds/states';
  import AgentBadge from '../lib/ds/AgentBadge.svelte';
  import ServerStatusIcon from '../ServerStatusIcon.svelte';
  import { cn } from '../lib/ui/utils';

  /**
   * The desktop sidebar: every server with its workspaces and their state. The list holds still while a
   * pointer is down and for 2 s after it scrolls (ui-never-moves-under-the-finger); a state change shows in place.
   */
  let { groups, activeKey = null, onopen, onserver, onnew, onrename, oncloseworkspace, onmove, width, id, class: className = '' }: {
    groups: NavGroup[]; activeKey?: string | null; onopen: (key: string) => void; onserver: (id: string) => void;
    onnew?: (serverId: string) => void; onrename?: (key: string, name: string) => void; oncloseworkspace?: (key: string) => void; /** Drop `key` beside `target.key`: the sidebar's own drag, or the arrow keys on a row's handle. */ onmove?: (key: string, target: { key: string; side: 'before' | 'after' }) => void; width?: number; id?: string; class?: string;
  } = $props();

  let collapsed = $state(new Set<string>());
  const toggleServer = (id: string) => { const next = new Set(collapsed); if (next.has(id)) next.delete(id); else next.add(id); collapsed = next; };
  const held = new HeldView();
  let rev = $state(0);
  let timer: ReturnType<typeof setTimeout> | undefined;
  const shown = $derived.by(() => { void rev; return held.view(groups, Date.now()); });
  // A mouse button can be released anywhere, so the release is heard on the window, not on the list.
  function down(e: PointerEvent) {
    const id = e.pointerId;
    held.down(id);
    const release = (ev: PointerEvent) => {
      if (ev.pointerId !== id) return;
      window.removeEventListener('pointerup', release, true);
      window.removeEventListener('pointercancel', release, true);
      held.up(id);
      rev++;
    };
    window.addEventListener('pointerup', release, true);
    window.addEventListener('pointercancel', release, true);
  }
  function scrolled() {
    held.scrolled(Date.now());
    clearTimeout(timer);
    timer = setTimeout(() => rev++, 2050); // the hold ends 2 s after the last scroll: show what arrived meanwhile
  }
  onDestroy(() => clearTimeout(timer));
  // Rename in place: a double click, a click on the name of the open workspace, F2, or the pencil. Enter saves, Esc or leaving the field cancels.
  let editing = $state<string | null>(null);
  let draft = $state('');
  function startEdit(key: string, name: string) { if (!onrename) return; editing = key; draft = name; }
  function commit(key: string, name: string) {
    if (editing !== key) return;
    editing = null;
    const next = draft.trim();
    if (next && next !== name) onrename?.(key, next);
  }
  const focusSelect = (el: HTMLInputElement) => { el.focus(); el.select(); };
  function editKey(e: KeyboardEvent, key: string, name: string) {
    if (e.key === 'Enter') { e.preventDefault(); commit(key, name); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); editing = null; }
  }
  function rowKey(e: KeyboardEvent, key: string, name: string) {
    if (e.key === 'F2' && onrename) { e.preventDefault(); startEdit(key, name); }
  }
  // Reordering by the handle (the same dragger as the phone list): the row follows the pointer and the list changes on release.
  let scroller: HTMLElement | undefined = $state();
  let drag: DragView | null = $state(null);
  let announcement = $state('');
  const dragger = new RowDragger({
    scroller: () => scroller,
    onview: (v) => (drag = v),
    ondrop: (key, target) => {
      onmove?.(key, target);
      const name = groups.flatMap((g) => g.workspaces).find((s) => s.key === key)?.name ?? '';
      const mine = groups.find((g) => g.workspaces.some((s) => s.key === key))?.workspaces ?? [];
      const rest = mine.filter((s) => s.key !== key);
      const at = rest.findIndex((s) => s.key === target.key);
      if (at >= 0) announcement = `${name} moved to position ${at + (target.side === 'after' ? 2 : 1)} of ${mine.length}`;
    },
  });
  $effect(() => () => dragger.destroy());
</script>

<aside {id} aria-label="Workspace" data-shell="sidebar" style:width={width ? `${width}px` : undefined} class={cn('flex shrink-0 flex-col border-r border-line bg-panel', !width && 'w-64', className)}>
  <div id="sidebar-servers" class="min-h-0 flex-1 overflow-y-auto border-t border-line px-2 pb-2" data-workspaces-scroll bind:this={scroller} onscroll={scrolled} onpointerdowncapture={down}>
    <h2 class="pt-3 pl-1 text-xs font-semibold tracking-wider text-soft uppercase">Servers</h2>
    {#each shown as g (g.server.id)}
      <section aria-label={g.server.server.server_name || 'Server'} data-server={g.server.id}>
        <div class="mt-1 flex items-center gap-2">
        <button type="button" aria-expanded={!collapsed.has(g.server.id)} aria-controls={`server-children-${g.server.id}`} onclick={() => toggleServer(g.server.id)} class="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-control bg-transparent px-3 text-left">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true" class="shrink-0 text-muted"><path d={collapsed.has(g.server.id) ? 'm9 6 6 6-6 6' : 'm6 9 6 6 6-6'} /></svg>
          <ServerStatusIcon state={g.server.state} name={g.server.server.server_name || 'Server'} />
          <span class="truncate text-base font-semibold text-fg">{g.server.server.server_name || 'Server'}</span>
        </button>
        {#if onnew}
          <button type="button" aria-label={`New workspace on ${g.server.server.server_name || 'Server'}`} title="New workspace" onclick={() => onnew?.(g.server.id)}
            disabled={g.server.state !== 'connected'} class="icon-button bg-transparent p-0 text-title text-accent-text"><Icon name="plus" /></button>
        {/if}
        </div>
        <div id={`server-children-${g.server.id}`} hidden={collapsed.has(g.server.id)}>
        {#each g.workspaces as s (s.key)}
          {@const grip = !!onmove && g.workspaces.length > 1}
          {@const dragging = drag?.key === s.key}
          <div data-row={s.key} data-list={g.server.id} class={cn('group relative flex h-11 w-full items-center rounded-control', s.key === activeKey && 'bg-raised')} class:dragging
            class:drop-before={drag?.target?.key === s.key && drag?.target?.side === 'before'} class:drop-after={drag?.target?.key === s.key && drag?.target?.side === 'after'}
            style:transform={dragging ? `translateY(${drag?.dy ?? 0}px)` : undefined}>
            {#if grip && editing !== s.key}
              <button type="button" class="grip row-action" data-testid="workspace-grip" data-grip aria-label={`Reorder ${s.name}. Drag, or use the up and down arrow keys`}
                onpointerdown={(e) => dragger.down(e, s.key)} onpointermove={(e) => dragger.move(e)} onpointerup={(e) => dragger.up(e)} onpointercancel={(e) => dragger.up(e, true)} onkeydown={(e) => dragger.key(e, s.key)} oncontextmenu={(e) => e.preventDefault()}>
                <svg viewBox="0 0 12 18" width="10" height="15" aria-hidden="true" fill="currentColor"><circle cx="3" cy="3" r="1.5" /><circle cx="9" cy="3" r="1.5" /><circle cx="3" cy="9" r="1.5" /><circle cx="9" cy="9" r="1.5" /><circle cx="3" cy="15" r="1.5" /><circle cx="9" cy="15" r="1.5" /></svg>
              </button>
            {/if}
            {#if editing === s.key}
              <div class="flex min-w-0 flex-1 items-center gap-2 pr-3 pl-6">
                <AgentBadge agent={s.agent} state={s.state} size={20} />
                <input use:focusSelect bind:value={draft} aria-label="Workspace name" maxlength="64" autocomplete="off" spellcheck="false" data-rename={s.key}
                  onkeydown={(e) => editKey(e, s.key, s.name)} onblur={() => commit(s.key, s.name)}
                  class="h-11 min-w-0 flex-1 rounded-control border border-accent bg-bg px-2 text-base text-fg outline-none" />
              </div>
            {:else}
              <button type="button" data-key={s.key} aria-current={s.key === activeKey ? 'true' : undefined} onclick={() => onopen(s.key)} onkeydown={(e) => rowKey(e, s.key, s.name)}
                class={cn('flex h-11 min-w-0 flex-1 items-center gap-2 rounded-control bg-transparent pr-3 text-left', grip ? 'pl-1' : 'pl-6')}>
                <AgentBadge agent={s.agent} state={s.state} size={20} />
                <span class="flex min-w-0 flex-1 flex-col" role="presentation"><span class="truncate text-sm text-fg" title={s.name}>{s.name}</span><small class={cn('text-xs font-normal', STATES[s.state].text)}>{STATES[s.state].label}</small></span>
              </button>
              {#if onrename || oncloseworkspace}<span class="row-actions row-action">
              {#if onrename}
                <button type="button" aria-label={`Rename ${s.name}`} title="Rename (F2)" data-rename-button={s.key} onclick={() => startEdit(s.key, s.name)}
                  class="icon-button bg-transparent text-soft"><Icon name="edit" size={18} /></button>
              {/if}
              {#if oncloseworkspace}
                <button type="button" aria-label={`Close ${s.name}`} title="Close workspace" data-close-button={s.key} onclick={() => oncloseworkspace?.(s.key)}
                  class="icon-button bg-transparent text-soft"><Icon name="close" size={18} /></button>
              {/if}
              </span>{/if}
            {/if}
          </div>
        {:else}
          <p class="py-2 pl-6 text-sm text-muted">No workspaces</p>
        {/each}
        </div>
      </section>
    {/each}
    <div class="sr-only" role="status" aria-live="polite">{announcement}</div>
  </div>

</aside>

<style>
  aside { background: var(--sunken); }
  /* Row actions show on hover and keyboard focus, and always where there is no hover (a touch screen). */
  .row-action { opacity: 0; }
  .row-actions { position: absolute; right: 0; top: 0; z-index: 1; display: flex; background: var(--sunken); border-radius: var(--radius-sm); }
  :global([data-row]:has([aria-current="true"])) .row-actions { background: var(--raised); }
  .row-actions :global(.icon-button) { --icon-fill: var(--bg); }
  :global([data-row]:hover) .row-action, :global([data-row]:focus-within) .row-action { opacity: 1; }
  .grip { flex: 0 0 44px; width: 44px; height: 44px; padding: 0; display: flex; align-items: center; justify-content: center; background: transparent; color: var(--fg-muted); border: 0; touch-action: none; user-select: none; -webkit-user-select: none; cursor: grab; }
  .dragging { z-index: 3; opacity: 0.95; filter: drop-shadow(0 4px 10px #0006); }
  .dragging .grip { cursor: grabbing; }
  .drop-before::before, .drop-after::after { content: ''; position: absolute; left: 0; right: 0; height: 2px; background: var(--accent, currentColor); }
  .drop-before::before { top: -1px; }
  .drop-after::after { bottom: -1px; }
  .row-action:hover { color: var(--fg); }
  @media (hover: none) { .row-action { opacity: 1; } }
</style>
